import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AgentChat from "../../components/AgentChat/AgentChat";
import RulesContext from "../../context/rules-context";
import {
    buildCompanyEventPayload,
    collectRulePieceKeys,
    fetchRulePieceDefinitions
} from "./RuleTree/ruleAgentClient";
import { translateOutcomeTreeData } from "./RuleTree/treeUtils";
import { useRuleArtifactRenderers } from "./ruleArtifactRenderer";

const RULE_IN_EDITION_LABEL = "Regra atual em edição";
const RULE_PIECE_IN_EDITION_LABEL = "Trecho de regra em edição";
const COMPANY_EVENT_CONTEXT_LABEL = "Contexto do evento da empresa";
const REFERENCED_RULE_PIECE_LABEL = "Trecho de regra referenciado: ";

function buildRuleEntry(ruleData) {
    return {
        text: ruleData.is_rule_piece ? RULE_PIECE_IN_EDITION_LABEL : RULE_IN_EDITION_LABEL,
        data: ruleData
    };
}

function buildCompanyEventEntry(payload) {
    return { text: COMPANY_EVENT_CONTEXT_LABEL, data: payload };
}

function buildRulePieceEntry(definition) {
    return {
        text: REFERENCED_RULE_PIECE_LABEL + definition.rule_piece_key,
        data: definition
    };
}

function RuleEngineAgentChat({
    treeData,
    userVariablesAvailable,
    ruleName,
    agentContext,
    onInsertGeneratedNodes,
    selectionAttachments,
    onSelectionAttachmentsChange,
    open,
    onPanelOpenChange
}) {
    const { t } = useTranslation();
    const rulesContext = useContext(RulesContext);

    const isRulePiece = Boolean(rulesContext.isRulePiece);
    const environment = rulesContext.environment;
    const companyKey = rulesContext.company_key;

    const ruleDataJson = useMemo(() => {
        let data = { tree_data: translateOutcomeTreeData(treeData) };
        if (userVariablesAvailable && userVariablesAvailable.length > 0) {
            data.user_variables = userVariablesAvailable;
        }
        if (ruleName) data.name = ruleName;
        if (isRulePiece) data.is_rule_piece = true;
        if (agentContext) data.agent_context = agentContext;
        return JSON.stringify(data);
    }, [treeData, userVariablesAvailable, ruleName, isRulePiece, agentContext]);

    const [rulePieceEntries, setRulePieceEntries] = useState([]);

    useEffect(() => {
        let keys = collectRulePieceKeys(JSON.parse(ruleDataJson).tree_data);
        if (keys.length === 0) {
            setRulePieceEntries([]);
            return undefined;
        }
        let cancelled = false;
        fetchRulePieceDefinitions({ keys: keys, environment: environment, companyKey: companyKey })
            .then((definitions) => {
                if (cancelled) return;
                setRulePieceEntries(definitions.map(buildRulePieceEntry));
            });
        return () => {
            cancelled = true;
        };
    }, [ruleDataJson, environment, companyKey]);

    const turnContext = useMemo(
        () => [buildRuleEntry(JSON.parse(ruleDataJson))].concat(rulePieceEntries),
        [ruleDataJson, rulePieceEntries]
    );

    const chatContext = useMemo(() => {
        let payload = buildCompanyEventPayload(rulesContext);
        return payload ? [buildCompanyEventEntry(payload)] : [];
    }, [rulesContext]);

    const renderers = useRuleArtifactRenderers({
        rulesContext: rulesContext,
        userVariablesAvailable: userVariablesAvailable,
        onInsertNodes: onInsertGeneratedNodes
    });

    const isEmptyTree = !treeData || treeData.length === 0;
    const suggestions = useMemo(
        () =>
            isEmptyTree
                ? [
                    t("Sugira validações comuns para este tipo de evento"),
                    t("Monte uma estrutura inicial para esta regra"),
                    t("Quais campos do evento posso usar?"),
                    t("Quais dados externos posso consultar?")
                ]
                : [
                    t("O que esta regra faz?"),
                    t("Explique a última alteração"),
                    t("Há algum problema nesta regra?"),
                    t("Como simplificar esta regra?")
                ],
        [isEmptyTree, t]
    );

    return (
        <AgentChat
            chatContext={chatContext}
            turnContext={turnContext}
            renderers={renderers}
            attachments={selectionAttachments}
            onAttachmentsChange={onSelectionAttachmentsChange}
            open={open}
            onPanelOpenChange={onPanelOpenChange}
            alwaysNewChat
            title={t("Assistente de Regras")}
            greeting={t("Olá! Como posso ajudar com esta regra?")}
            emptySubtitle={
                isEmptyTree
                    ? t("Peça sugestões ou gere validações para começar a montar sua regra.")
                    : t("Tire dúvidas ou peça análises sobre a regra que você está editando.")
            }
            suggestions={suggestions}
        />
    );
}

export default RuleEngineAgentChat;
